// Landing page at myumaps.ca/ — a support call-to-action card. Visitors can
// back UMaps with their UManitoba email or Instagram follow, then continue into
// the campus pathfinder at /umanitoba. Old shared-route deep links that still
// point at "/" are forwarded to /umanitoba with their query intact.

import { useEffect, useState } from 'react';
import UMapsWordmark from './components/UMapsWordmark.jsx';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const ALLOWED = /^[^\s@]+@(myumanitoba\.ca|umanitoba\.ca)$/i;
const IG_URL = 'https://www.instagram.com/myumaps/';
const APP_PATH = '/umanitoba';

function logEmail(email) {
  try {
    fetch(`${API_BASE}/api/collect-email`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }), keepalive: true,
    }).catch(() => {});
  } catch { /* never block the UI on logging */ }
}

export default function LandingPage() {
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  // Forward legacy "/?from=&b=&r=" share links to the pathfinder's new path.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get('b') && p.get('r')) window.location.replace(`${APP_PATH}${window.location.search}`);
    } catch { /* ignore */ }
  }, []);

  const submit = () => {
    const v = email.trim();
    if (!ALLOWED.test(v)) { setErr('Please use your @myumanitoba.ca or @umanitoba.ca email.'); return; }
    logEmail(v.toLowerCase());
    setDone(true);
  };

  const openMap = () => { window.location.href = APP_PATH; };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <UMapsWordmark size={30} href={null} style={{ fontSize: 26, color: '#1a73e8', letterSpacing: '-0.02em' }} />
        <div style={S.tagline}>Find your way around the University of Manitoba.</div>

        <div style={S.support}>
          <div style={S.supportTitle}>Support UMaps by signing up with your school e-mail.</div>
          {done ? (
            <div style={S.thanks}>Thanks for your support! 🎉</div>
          ) : (
            <>
              <div style={S.row}>
                <input
                  style={S.input}
                  type="email"
                  autoComplete="email"
                  maxLength={160}
                  placeholder="you@myumanitoba.ca"
                  value={email}
                  onChange={e => { setEmail(e.target.value); if (err) setErr(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') submit(); }}
                />
                <button style={S.submit} disabled={!email.trim()} onClick={submit}>Sign up</button>
              </div>
              {err && <div style={S.err}>{err}</div>}
            </>
          )}

          <a style={S.ig} href={IG_URL} target="_blank" rel="noopener noreferrer">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <defs>
                <radialGradient id="ig-grad-land" cx="30%" cy="107%" r="150%">
                  <stop offset="0%" stopColor="#fdf497" />
                  <stop offset="5%" stopColor="#fdf497" />
                  <stop offset="45%" stopColor="#fd5949" />
                  <stop offset="60%" stopColor="#d6249f" />
                  <stop offset="90%" stopColor="#285AEB" />
                </radialGradient>
              </defs>
              <rect x="2" y="2" width="20" height="20" rx="6" ry="6" fill="url(#ig-grad-land)" />
              <circle cx="12" cy="12" r="4.5" stroke="#fff" strokeWidth="1.8" fill="none" />
              <circle cx="17.5" cy="6.5" r="1.2" fill="#fff" />
            </svg>
            Follow @myumaps
          </a>
        </div>

        <button style={S.openMap} onClick={openMap}>Open the Map →</button>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, boxSizing: 'border-box',
    background: 'linear-gradient(160deg, #1a73e8 0%, #0b3d91 100%)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
  },
  card: {
    width: 420, maxWidth: '100%', background: '#fff', borderRadius: 20,
    padding: 26, boxShadow: '0 12px 40px rgba(0,0,0,0.3)', boxSizing: 'border-box',
  },
  brand: { fontSize: 30, fontWeight: 900, color: '#1a73e8', letterSpacing: '-0.02em' },
  tagline: { fontSize: 15, color: '#5f6368', marginTop: 4, marginBottom: 18 },
  support: { background: '#f1f5fd', border: '1px solid #dce6fb', borderRadius: 14, padding: 16, marginBottom: 16 },
  supportTitle: { fontSize: 14, fontWeight: 700, color: '#202124', marginBottom: 10 },
  row: { display: 'flex', gap: 8 },
  input: {
    flex: 1, minWidth: 0, boxSizing: 'border-box', fontSize: 16, padding: '11px 12px',
    border: '1.5px solid #dadce0', borderRadius: 10, outline: 'none', background: '#fff',
  },
  submit: {
    flexShrink: 0, border: 'none', borderRadius: 10, padding: '0 16px',
    background: '#1a73e8', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer',
  },
  err: { marginTop: 8, background: '#fce8e6', color: '#c5221f', borderRadius: 8, padding: '7px 10px', fontSize: 13 },
  thanks: { background: '#e6f4ea', color: '#137333', borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 700, textAlign: 'center' },
  ig: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12,
    textDecoration: 'none', color: '#3c4043', fontSize: 14, fontWeight: 700,
    border: '1.5px solid #dadce0', borderRadius: 10, padding: '10px 12px', background: '#fff',
  },
  openMap: {
    width: '100%', border: 'none', borderRadius: 12, padding: 15,
    background: '#1a73e8', color: '#fff', fontSize: 17, fontWeight: 800, cursor: 'pointer',
  },
};
